import { SocialProofStrip } from "@/components/features/ClubImport/SocialProofStrip";
import {
  BLUE_BANNER_ACCENT,
  BannerPoints,
  FancyBanner,
  bannerSurfaces,
  useSnoozedDismissal,
} from "@/components/molecules/FancyBanner/FancyBanner";
import { Button } from "@/design-system/Button/Button";
import CloseButton from "@/design-system/SpecificButtons/CloseButton/CloseButton";
import { cn } from "@/lib/utils";
import { Trans, useTranslation } from "@repo/shared/i18n";
import { Smartphone } from "lucide-react";
import { useNavigate } from "react-router";

import { GooglePlayBadge } from "./GooglePlayBadge";
import { useAppOffer } from "./appOffer";

const POINTS = ["importClub", "onTheGo", "native"] as const;
const SURFACES = bannerSurfaces(BLUE_BANNER_ACCENT);

const Title = ({ beta }: { beta: boolean }) => {
  const { t } = useTranslation("appLanding");
  return (
    <Trans
      t={t}
      i18nKey={beta ? "promo.betaTitle" : "promo.title"}
      components={[<em className="not-italic text-primary-washed-blue" />]}
    />
  );
};

const Points = ({ direction }: { direction: "row" | "column" }) => {
  const { t } = useTranslation("appLanding");
  return (
    <BannerPoints
      points={POINTS.map((key) => t(`promo.points.${key}`))}
      direction={direction}
      numberClassName="text-primary-washed-blue"
    />
  );
};

/**
 * Front page app promo, laid out like the Gallery banner: the Android beta's
 * Play listing where it is live (see appOffer), the waitlist everywhere else.
 */
export const AppPromoBanner = ({ className }: { className?: string }) => {
  const offer = useAppOffer();
  if (!offer) return null;
  // Keyed: the dismissal state is read once, for the offer it mounted with.
  return (
    <PromoBanner key={offer} beta={offer === "beta"} className={className} />
  );
};

const PromoBanner = ({
  beta,
  className,
}: {
  beta: boolean;
  className?: string;
}) => {
  const { t } = useTranslation("appLanding");
  const navigate = useNavigate();
  // Its own key, so snoozing the waitlist doesn't hide the beta launch.
  const { hidden, dismissing, dismiss, handleTransitionEnd } =
    useSnoozedDismissal(
      beta ? "appBetaBannerDismissedAt" : "appPromoBannerDismissedAt",
    );

  if (hidden) return null;

  // The beta is the store badge alone: no selling points, no "Used by pros"
  // strip, never stretched.
  const cta = (stretch?: boolean) =>
    beta ? (
      <GooglePlayBadge />
    ) : (
      <Button
        variant="primary"
        size="medium"
        stretch={stretch}
        onClick={() => navigate("/app")}
        className={cn(
          "font-semibold whitespace-nowrap",
          !stretch && "shrink-0",
        )}
      >
        {t("form.submit")}
      </Button>
    );

  return (
    <>
      <FancyBanner
        accent={BLUE_BANNER_ACCENT}
        icon={<Smartphone aria-hidden className="size-6 text-blue-400" />}
        title={<Title beta={beta} />}
        footer={
          !beta && (
            <div className="mt-3.5">
              <Points direction="row" />
            </div>
          )
        }
        strip={!beta && <SocialProofStrip />}
        cta={cta()}
        dismissLabel={t("promo.dismiss")}
        onDismiss={dismiss}
        onTransitionEnd={handleTransitionEnd}
        dismissing={dismissing}
        className={className}
      />

      <section
        style={{ ...SURFACES.vars, ...SURFACES.panel }}
        onTransitionEnd={handleTransitionEnd}
        className={cn(
          "relative overflow-hidden rounded-2xl touch-device:hidden",
          "border border-[rgba(var(--banner-base),.18)]",
          "shadow-[0_22px_60px_-30px_rgba(var(--banner-dot),.5)]",
          "transition-[opacity,transform] duration-200 ease-out",
          dismissing && "translate-y-2 opacity-0",
          className,
        )}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={SURFACES.dotField}
        />
        <CloseButton
          aria-label={t("promo.dismiss")}
          onClick={dismiss}
          className="absolute right-2 top-2 z-10 border-transparent bg-transparent text-white hover:bg-white/10"
        />
        <div className="relative flex flex-col items-start gap-4 px-5 py-6">
          <h3 className="pr-8 font-sans text-xl font-semibold leading-tight tracking-tight text-neutral-50">
            <Title beta={beta} />
          </h3>
          {!beta && <Points direction="column" />}
          {cta(true)}
        </div>
        {!beta && <SocialProofStrip fitOneLine className="px-5" />}
      </section>
    </>
  );
};
